<template>
    <div class="synthetic-objects">
        <div class="synthetic-objects__header" v-if="!isFvt || (isFvt && objectsList.length === 0)">
            <p class="synthetic-objects__title" :class="{'synthetic-objects__title--hidden': isFvt}">
                {{ isFvt ? '' : $t.userDefinedObjects }}
            </p>
            <button class="synthetic-objects__action link" @click="addUserDefinedObject()">
                <i class="icon-plus"></i><span>{{ $t.addUserDefinedObject }}</span>
            </button>
        </div>
        <div class="synthetic-objects__list-wrapper">
            <ul v-if="isScd" class="synthetic-objects__list">
                <li class="synthetic-objects__item" v-for="(item, index) of objectsList" :key="index">
                    {{ displayName(item) }}
                    <div class="synthetic-objects__buttons">
                        <button
                            class="synthetic-objects__button synthetic-objects__button--edit tertiary"
                            @click="handleObjectEdit(item, index)"
                        >
                            {{ $t.edit }}
                        </button>
                        <button
                            class="synthetic-objects__button synthetic-objects__button--remove tertiary"
                            @click="handleObjectRemove(item)"
                        >
                            {{ $t.remove }}
                        </button>
                    </div>
                </li>
            </ul>
            <div
                v-if="(isOvt || isSovt) && objectsList.length"
                class="ovt__objects-table__content"
                :class="{ loading }"
            >
                <div class="d-table-row headers">
                    <div class="d-table-cell">{{ $t.object }}</div>
                    <div class="d-table-cell-empty"></div>
                    <div class="d-table-cell orbit-switcher label">
                        {{ $t.orbitSwitchLabel }}
                        <Tooltip position="left" :tooltipText="$keys.t.switchOrbit"
                            ><i class="icon-info"
                        /></Tooltip>
                    </div>
                    <div class="d-table-cell">{{ $t.show }}</div>
                </div>
                <div class="inner">
                    <div
                        class="d-table-row item"
                        v-for="(item, index) of objectsList"
                        :key="index"
                        :class="{ 'item-highlight': isSelectedObject(item) }"
                    >
                        <div class="d-table-cell">
                            <input
                                type="radio"
                                :id="'radio_' + item.designator"
                                :value="item.designator"
                                name="radioGroup"
                                :checked="isSelectedObject(item)"
                                @change="handleSelection(item)"
                            />
                        </div>
                        <div class="d-table-cell">
                            <span @click="handleSelection(item)">
                                {{ displayName(item) }}
                            </span>
                            <div class="synthetic-objects__buttons">
                                <button
                                    class="synthetic-objects__button synthetic-objects__button--edit tertiary"
                                    @click="handleObjectEdit(item, index)"
                                >
                                    {{ $t.edit }}
                                </button>
                                <button
                                    class="synthetic-objects__button synthetic-objects__button--duplicate tertiary"
                                    @click="handleObjectDuplicate(item)"
                                >
                                  {{ $t.duplicate }}
                                </button>
                                <button
                                    class="synthetic-objects__button synthetic-objects__button--remove tertiary"
                                    @click="handleObjectRemove(item)"
                                >
                                    {{ $t.remove }}
                                </button>
                            </div>
                        </div>
                        <div class="d-table-cell orbit-switcher field">
                            <input
                                class="toggle-switch"
                                :id="`perturbedOrbit_${item.designator}`"
                                :checked="isPerturbedOn(item)"
                                :disabled="item.keplerianOnly"
                                type="checkbox"
                                @click="orbitSwitchOnChange(item, $event.target.checked, $event)"
                            />
                            <label
                                class="toggle-switch__button"
                                :class="{ disabled: item.keplerianOnly }"
                                :for="`perturbedOrbit_${item.designator}`"
                            ></label>
                        </div>
                        <div class="d-table-cell input">
                            <input
                                type="checkbox"
                                :id="item.name"
                                :disabled="isSovt ? false : !isObjectCalculated(item)"
                                :checked="isCheckedObject(item)"
                                @change="checkObject(item)"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="isFvt && objectsList.length">
                <p class="synthetic-objects__single-item" :class="{'synthetic-objects__single-item--active': isSelectedSyntheticFvtObject}" v-for="(item, index) of objectsList" :key="index">
                    {{ displayName(item) }}
                        <button
                            class="synthetic-objects__button synthetic-objects__button--edit tertiary"
                            @click="handleObjectEdit(item, index)"
                        >
                            {{ $t.edit }}
                        </button>
                </p>
            </div>
        </div>
        <p v-if="!isUserDefinedObjectCustomObservatoryEnabled && isScd" class="synthetic-objects__info">
            {{ $t.noCustomObservatoryForUserDefinedObjects }}
        </p>
    </div>
</template>

<script>
import Tooltip from "@/components/common/Tooltip";
import PopupService from "@/services/popup.service";
import SynodicCalculationService from "@/services/synodic-calculation.service";
import SyntheticObjectsService from "@/services/synthetic-objects.service";
import UtilsService from "@/services/utils.service";
import VisualisationService from "@/services/visualisation.service";
import Config from "@/constants/config";

export default {
    name: "SyntheticObjects",
    props: {
        tool: String,
    },
    components: {
        Tooltip,
    },
    computed: {
        objectsList() {
            let objectsList = []
            if (this.isFvt) {
              objectsList = SyntheticObjectsService.getSyntheticFvtObjectsList();
              objectsList.forEach(item => Object.assign(item, {type: SyntheticObjectsService.OBJECT_TYPES.fvt}))
            } else if (this.isScd) {
              objectsList = SyntheticObjectsService.getSyntheticDefaultObjectsList();
              objectsList.forEach(item => Object.assign(item, {type: SyntheticObjectsService.OBJECT_TYPES.scd}))
            } else if (this.isSovt || this.isOvt) {
              let ellipticalObjectsList = SyntheticObjectsService.getEllipticalObjectsList();
              ellipticalObjectsList.forEach(item => Object.assign(item, {type: SyntheticObjectsService.OBJECT_TYPES.elliptical}))
              let hyperbolicObjectsList = SyntheticObjectsService.getHyperbolicObjectsList();
              hyperbolicObjectsList.forEach(item => Object.assign(item, {type: SyntheticObjectsService.OBJECT_TYPES.hyperbolic}))
              objectsList = [...ellipticalObjectsList, ...hyperbolicObjectsList];
            }
            return objectsList;
        },

        isUserDefinedObjectCustomObservatoryEnabled() {
            return SyntheticObjectsService.isUserDefinedObjectCustomObservatoryEnabled();
        },

        isChecked() {
            return VisualisationService.getIsChecked();
        },

        selectedObjectName() {
            return VisualisationService.getSelectedObjectName();
        },

        selectedSynodicObjectName() {
            return VisualisationService.getSelectedSynodicObjectName();
        },

        visibleRealSynodicObjectsList() {
            return SynodicCalculationService.getVisibleRealSynodicObjectsList();
        },

        loading() {
            return VisualisationService.loadingObjectsEphemerides().length;
        },

        isSelectedSyntheticFvtObject() {
            return SyntheticObjectsService.checkIsSyntheticObjectFlybyCalculated();
        },

        isScd() {
            return this.tool === "scd";
        },

        isOvt() {
            return this.tool === "ovt";
        },

        isSovt() {
            return this.tool === "sovt";
        },

        isFvt() {
            return this.tool === 'fvt';
        }
    },
    methods: {
        addUserDefinedObject() {
            if (this.isFvt || this.isScd) {
              PopupService.show({
                component: "PopupSyntheticObjectConfiguration",
              });
            } else {
              PopupService.show({
                component: "PopupTabsObjectConfiguration",
              });
            }
        },

        handleObjectEdit(object, objectIndex) {
          if (this.isFvt || this.isScd) {
            PopupService.show({
              component: "PopupSyntheticObjectConfiguration",
              editedObjectIndex: objectIndex,
            });
          } else {
            PopupService.show({
              component: "PopupTabsObjectConfiguration",
              selectedObject: object,
              editedObjectIndex: objectIndex,
              objectType: object.type
            });
          }
        },

        handleObjectRemove(object) {
            PopupService.show({
                component: "PopupSyntheticObjectDelete",
                selectedObject: object,
                removeByObject: Config.removeByObject.userDefined,
                objectType: object.type,
            });
        },

        async handleObjectDuplicate(object) {
          let syntheticObjectNew = UtilsService.deepCopy(object);
          let syntheticObjectsList = SyntheticObjectsService.getSyntheticObjectsList(object.type);
          let objectType = object.type === SyntheticObjectsService.OBJECT_TYPES.elliptical ? SyntheticObjectsService.OBJECT_TYPES.elliptical : SyntheticObjectsService.OBJECT_TYPES.hyperbolic
          if (syntheticObjectsList.length === 1) {
            syntheticObjectNew.objectName = object.objectName + ' - duplicate';
          } else {
            const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const DELIMITER =  /^(.+?)(?=\s*-\s*copy\b|\s*(?:-\s*|\s+)\d+\s*(?:-\s*)?duplicate\b|\s*-\s*duplicate\b)/i;
            const getBaseName = (name) => {
              const s = String(name ?? '');
              const m = s.match(DELIMITER);
              return m ? m[1].trim() : s.trim();
            };
            const nextCopyName = (
                names, base,
                {treatUnnumberedAs1 = true} = {}
            ) => {
              const existsCopy = /\bcopy\b/i.test(String(object.objectName ?? ''));

              let re;
              if (existsCopy)
                re = new RegExp(`^${escapeRegex(base)}\\s*-\\s*copy(?:\\s+(\\d+))?(?:\\s*(?:-\\s*)?duplicate)?$`,'i');
              else
                re = new RegExp(`^${escapeRegex(base)}(?:\\s*(?:-\\s*|\\s+)(\\d+))?\\s*(?:-\\s*)?duplicate\\s*$`,'i');

              let maxNum = 0;
              for (const name of names) {
                const m = name.match(re);
                if (!m) continue;
                if (m[1]) {
                  const n = parseInt(m[1], 10);
                  if (!Number.isNaN(n) && n > maxNum) maxNum = n;
                } else if (treatUnnumberedAs1) {
                  if (1 > maxNum) maxNum = 1;
                }
              }
              const copy = existsCopy ? ' - copy ' : ' ';
              const suffix = ' - duplicate';
              return `${base}${copy}${maxNum === 0 ? '' : maxNum + 1}${suffix}`;
            };
            const base = getBaseName(object.objectName);
            const names = syntheticObjectsList.map(i => i.objectName);
            const newName = nextCopyName(names, base);
            syntheticObjectNew.objectName = newName;
          }
          syntheticObjectNew.designator = SyntheticObjectsService.createSyntheticObjectDesignator(syntheticObjectNew.objectName);
          syntheticObjectsList.push(syntheticObjectNew);
          let parameters = {}
          if (objectType === SyntheticObjectsService.OBJECT_TYPES.elliptical) {
            SyntheticObjectsService.setEllipticalObjectsList(syntheticObjectsList);
            parameters = {
              referenceEpoch: null,
              perihelionDistance: null,
              aphelionDistance: null,
              inclination: null,
              rightAscension: null,
              perihelionArgument: null,
              meanAnomaly: null,
              absoluteMagnitude: null,
              slopeParameter: null,
              magnitudeLimit: 18,
            }
          }
          if (objectType === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) {
            SyntheticObjectsService.setHyperbolicObjectsList(syntheticObjectsList);
            parameters = {
              referenceEpoch: null,
              perihelionDistance: null,
              inclination: null,
              ascendingNodeLongitude: null,
              perihelionArgument: null,
              pericentrePassage: null,
              absoluteMagnitude: null,
              slopeParameter: null,
              magnitudeLimit: 18,
            };
          }
          if (this.isSovt) {
            const currentObjectData = SyntheticObjectsService.getSyntheticObjectParameters(syntheticObjectNew.designator, objectType);
            const isOrbitAvailable = await SynodicCalculationService.createSynodicOrbit(
                parameters,
                currentObjectData,
                syntheticObjectNew.designator
            );
            if (!isOrbitAvailable) {
              return
            }
            const calculatedObject = {
              designator: syntheticObjectNew.designator,
              objectData: currentObjectData,
              magnitudeLimit: parameters.magnitudeLimit,
            }
            const calculatedObjects = UtilsService.deepCopy(SynodicCalculationService.getCalculatedRealSynodicObjectsList());
            calculatedObjects.push(calculatedObject)
            await SynodicCalculationService.setCalculatedRealSynodicObjectsList(calculatedObjects);
          }
          this.checkObject(syntheticObjectNew);
          this.isCheckedObject(syntheticObjectNew);
        },

        displayName(item) {
            return SyntheticObjectsService.getSyntheticObjectName(item);
        },

        isSelectedObject(item) {
            const { designator } = item;
            if (this.isOvt) {
                return designator === this.selectedObjectName;
            }
            if (this.isSovt) {
                return designator === this.selectedSynodicObjectName;
            }
        },

        handleSelection(item) {
            if (this.isOvt) {
                this.selectObject(item);
                this.toggleSelected(item);
                return;
            }
            if (this.isSovt) {
                const { designator } = item;
                VisualisationService.selectSynodicObject(designator);
            }
        },

        selectObject(item) {
            const designator = item.designator;
            const isPerturbed = this.isOvtPerturbedOrbit(designator) || item.perturbedOn;
            this.selectedObjectName = designator === this.selectedObjectName ? "" : designator;
            VisualisationService.selectObject(designator, isPerturbed, item.type);
        },

        toggleSelected(item) {
            this.isSelectedObject(item) ? (this.selectedObjectName = "") : (this.selectedObjectName = item.designator);
        },

        async orbitSwitchOnChange(item, isChecked, event) { /* eslint-disable-line */
            const { designator } = item;
            if (this.isOvt) {
                item.perturbedOn = isChecked;
                if (this.isCheckedObject(item)) {
                    this.checkObject(item, true);
                    return;
                }
                this.handleOvtSyntheticObjectPerturbedOrbit(designator, isChecked);
            }
            if (this.isSovt) {
                let isPerturbedAvailable = true;
                const isVisible = SynodicCalculationService.getVisibleRealSynodicObjectsList().includes(designator);

                if (!isChecked && isVisible) {
                    // User switched from perturbed → keplerian
                    SynodicCalculationService.switchOrbitToKeplerian(designator);
                }

                if (isChecked && isVisible) {
                    // Try to switch to perturbed; returns false if BE failed or no data
                    isPerturbedAvailable = await SynodicCalculationService.switchOrbitToPerturbed(designator, item.type);
                }

                isVisible && SynodicCalculationService.checkIsFocusOnObject(designator);

                // Sync local perturbed list with the actual state
                const perturbedOrbitList = UtilsService.deepCopy(
                    SynodicCalculationService.getPerturbedSynodicOrbitList()
                );

                if (isChecked && isPerturbedAvailable) {
                    // Perturbed available → keep in list
                    if (!perturbedOrbitList.includes(designator)) {
                        perturbedOrbitList.push(designator);
                    }
                } else {
                    // Either user turned it off, or perturbed is NOT available:
                    // ensure it is removed from list (i.e. back to keplerian)
                    const objectIndex = perturbedOrbitList.indexOf(designator);
                    objectIndex !== -1 && perturbedOrbitList.splice(objectIndex, 1);

                    // Force the toggle visually back to "off"
                    event.target.checked = false;
                }

                await SynodicCalculationService.setPerturbedSynodicOrbitList(perturbedOrbitList);

                // We already show the popup inside handlePerturbedUnavailable(),
                // which is called from switchOrbitToPerturbed, so no extra popup here.
            }
        },

        isObjectCalculated(item) {
            if (this.isOvt) {
                return true;
            }
            if (this.isSovt) {
                const { designator } = item;
                const calculatedObjectList = SynodicCalculationService.getCalculatedRealSynodicObjectsList();
                return !!UtilsService.findItemInObjectList("designator", designator, calculatedObjectList);
            }
        },

        handleOvtSyntheticObjectPerturbedOrbit(designator, isPerturbed) {
            const perturbedObjectsList = VisualisationService.getIsPerturbed();
            const perturbedListIndex = perturbedObjectsList.indexOf(designator);
            const isOnPerturbedList = perturbedListIndex !== -1;
            if ((isPerturbed && isOnPerturbedList) || (!isPerturbed && !isOnPerturbedList)) {
                return;
            }
            if (isPerturbed && !isOnPerturbedList) {
                perturbedObjectsList.push(designator);
                return;
            }
            perturbedObjectsList.splice(perturbedListIndex, 1);
        },

        isCheckedObject(item) {
            const { designator } = item;
            if (this.isOvt) {
                return this.isChecked.includes(designator);
            }
            if (this.isSovt) {
                return this.visibleRealSynodicObjectsList.includes(designator);
            }
        },

        async checkObject(item, reload = false) {
            const { designator } = item;
            if (this.isOvt) {
                VisualisationService.checkObject(designator, item.perturbedOn, reload, item.type);
                return;
            }
            if (this.isSovt) {
                await SynodicCalculationService.handleObjectVisibility(designator);
            }
        },

        isOvtPerturbedOrbit(designator) {
            const perturbedObjectsList = VisualisationService.getIsPerturbed();
            return perturbedObjectsList.includes(designator);
        },

        isPerturbedOn(object) {
            const { designator } = object;
            if (this.isOvt) {
                return object.perturbedOn || this.isOvtPerturbedOrbit(designator);
            }
            if (this.isSovt) {
                return SynodicCalculationService.getPerturbedSynodicOrbitList().includes(designator);
            }
        },
    },
    mounted() {},
};
</script>

<style lang="scss">
@import "@/styles/common/synthetic-objects.scss";
</style>
